import { useCallback, useState } from 'react';
import { sumBy } from 'lodash';
import { Card, CardBody, CardHeader, Icon } from '@repo/ui';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { dateUtil, numberUtil } from '@xtnd/web/shared';

import { Receipt } from '../../types';

interface ReceiptsTableProps {
  data: Receipt[];
  isLoading: boolean;
  error: Error | null;
}

export const ReceiptsTable = ({ data, isLoading, error }: ReceiptsTableProps) => {
  const [isExpand, setIsExpand] = useState<boolean>(true);
  const columns: DataTableColumn<Receipt>[] = [
    {
      key: 'date',
      header: 'Payment Date',
      align: 'left',
      renderCell: (recipient: Receipt) => {
        return (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {dateUtil.formatDate(new Date(recipient.date), dateUtil.DateFormat.US)}
          </span>
        );
      },
    },
    {
      key: 'accountType',
      header: 'Payment Source',
      align: 'left',
      renderCell: recipient => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light uppercase">
          {recipient.accountType}
        </span>
      ),
    },
    {
      key: 'fundType',
      header: 'Deposit Account',
      align: 'left',
      renderCell: recipient => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light uppercase">
          {recipient.fundType}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Payment Amount',
      align: 'right',
      renderCell: recipient => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(recipient.value)}
        </span>
      ),
    },
    {
      key: 'checkNumber',
      header: 'Reference',
      align: 'left',
      renderCell: recipient => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {recipient.checkNumber}
        </span>
      ),
    },
    {
      key: 'text',
      header: 'Description',
      align: 'left',
      renderCell: recipient => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {recipient.text}
        </span>
      ),
    },
  ];

  const renderHeader = () => (
    <button
      className="flex items-center justify-between w-full"
      onClick={() => setIsExpand(prev => !prev)}
      aria-label={isExpand ? 'Collapse receipts' : 'Expand receipts'}
    >
      <div className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Receipts</div>
      <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light cursor-pointer">
        {isExpand ? <Icon name="HiChevronDown" /> : <Icon name="HiChevronRight" />}
      </span>
    </button>
  );

  const renderFooter = useCallback(() => {
    const totalValue = sumBy(data ?? [], item => item?.value ?? 0);
    return (
      <>
        <tr className="border-xtnd-dark bg-xtnd-blue-100">
          <td
            key="applied"
            colSpan={3}
            className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light"
          >
            <span className="font-bold">Applied</span>
          </td>
          <td
            key="totalValue"
            colSpan={1}
            className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
          >
            <span className="font-bold">{numberUtil.formatCurrency(totalValue)}</span>
          </td>
          <td
            key=""
            colSpan={2}
            className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
          />
        </tr>
        <tr className="bg-xtnd-blue-100">
          <td
            key="total"
            colSpan={3}
            className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light"
          >
            <span className="font-bold">Total Receipts</span>
          </td>
          <td
            key="totalValue"
            colSpan={1}
            className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
          >
            <span className="font-bold">{numberUtil.formatCurrency(totalValue)}</span>
          </td>
          <td
            key=""
            colSpan={2}
            className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
          />
        </tr>
      </>
    );
  }, [data]);

  return (
    <Card>
      <CardHeader className={`${!isExpand ? '!border-none !mb-0 !pb-0' : ''}`}>
        {renderHeader()}
      </CardHeader>
      {isExpand && (
        <CardBody>
          <DataTable
            columns={columns}
            data={{
              data: data || [],
              total: data?.length ?? 0,
            }}
            isLoading={isLoading}
            error={error}
            tableContainerClassName="overflow-y-auto relative"
            tableClassName="min-w-full divide-y divide-gray-200 relative"
            stickyHeader={true}
            loadingRowCount={5}
            loadingColumnCount={7}
            renderFooter={renderFooter}
          />
        </CardBody>
      )}
    </Card>
  );
};
